"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { ToolRegistryItem } from "@/types/tools";

const FAVORITES_KEY = "localtools:favorites";
const RECENT_KEY = "localtools:recent";
const STORAGE_EVENT = "localtools-storage";

function readIds(key: string) {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const value = window.localStorage.getItem(key);
    const parsed: unknown = value ? JSON.parse(value) : [];

    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === "string")
      : [];
  } catch {
    return [];
  }
}

function writeIds(key: string, ids: string[]) {
  window.localStorage.setItem(key, JSON.stringify(ids));
  window.dispatchEvent(new CustomEvent(STORAGE_EVENT));
}

function useStoredIds(key: string) {
  const [ids, setIds] = useState<string[]>([]);

  useEffect(() => {
    const sync = () => setIds(readIds(key));

    sync();
    window.addEventListener("storage", sync);
    window.addEventListener(STORAGE_EVENT, sync);

    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(STORAGE_EVENT, sync);
    };
  }, [key]);

  return [ids, setIds] as const;
}

export function useFavorites() {
  const [favorites, setFavorites] = useStoredIds(FAVORITES_KEY);

  const toggleFavorite = useCallback(
    (toolId: string) => {
      setFavorites((current) => {
        const next = current.includes(toolId)
          ? current.filter((id) => id !== toolId)
          : [toolId, ...current];

        writeIds(FAVORITES_KEY, next);
        return next;
      });
    },
    [setFavorites],
  );

  const favoriteSet = useMemo(() => new Set(favorites), [favorites]);

  return {
    favorites,
    isFavorite: (toolId: string) => favoriteSet.has(toolId),
    toggleFavorite,
  };
}

export function addRecentTool(toolId: string) {
  if (typeof window === "undefined") {
    return;
  }

  const next = [toolId, ...readIds(RECENT_KEY).filter((id) => id !== toolId)].slice(0, 8);
  writeIds(RECENT_KEY, next);
}

export function useRecentTools(allTools: ToolRegistryItem[], limit = 6) {
  const [recent] = useStoredIds(RECENT_KEY);

  return recent
    .map((id) => allTools.find((tool) => tool.id === id))
    .filter((tool): tool is ToolRegistryItem => Boolean(tool))
    .slice(0, limit);
}
